export type SubscriptionErrorCode = "LIMIT_REACHED" | "FEATURE_REQUIRED";

export interface SubscriptionErrorData {
  error: string;
  code: SubscriptionErrorCode;
  current?: number;
  max?: number;
  upgradeRequired: boolean;
}

export class SubscriptionError extends Error {
  code: SubscriptionErrorCode;
  current?: number;
  max?: number;
  upgradeRequired: boolean;

  constructor(data: SubscriptionErrorData) {
    super(data.error);
    this.name = "SubscriptionError";
    this.code = data.code;
    this.current = data.current;
    this.max = data.max;
    this.upgradeRequired = data.upgradeRequired;
  }
}

export function isSubscriptionErrorData(
  data: unknown
): data is SubscriptionErrorData {
  if (typeof data !== "object" || data === null) return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.error === "string" &&
    (obj.code === "LIMIT_REACHED" || obj.code === "FEATURE_REQUIRED") &&
    typeof obj.upgradeRequired === "boolean"
  );
}

export function isSubscriptionError(
  error: unknown
): error is SubscriptionError {
  return error instanceof SubscriptionError;
}

const limitLabels: Record<string, string> = {
  documents: "documents",
  guilds: "servers",
  ticketCategories: "ticket categories",
  teamMembers: "team members",
  aiMessagesThisMonth: "AI messages",
};

const featureLabels: Record<string, string> = {
  customBot: "Custom Bot",
  knowledgeBase: "Knowledge Base",
  ticketSystem: "Ticket System",
  ticketTranscripts: "Ticket Transcripts",
  ticketClaiming: "Ticket Claiming",
};

export function getLimitLabel(key: string): string {
  return limitLabels[key] || key;
}

export function getFeatureLabel(key: string): string {
  return featureLabels[key] || key;
}

export function getLimitErrorMessage(error: SubscriptionError): string {
  return error.message || "You have reached the limit for this resource";
}

export function getFeatureErrorMessage(error: SubscriptionError): string {
  return error.message || "This feature requires a plan upgrade";
}

export function getErrorMessage(
  error: unknown,
  fallback = "Something went wrong"
): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return fallback;
}
